import * as ActionTypes from '../actions'
import { routerReducer as routing } from 'react-router-redux'
import { combineReducers } from 'redux'
import updeep from 'updeep'
import { Schemas } from '../middleware/api'

import get from 'lodash/get'
import keys from 'lodash/keys'
import first from 'lodash/first'
import values from 'lodash/values'

const createItemEntitiesReducer = (state, action) => {
  const { schema, response } = action

  switch(schema) {
  case Schemas.SAVED_ARTICLE:
    const savedArticle = first(values(response.entities.savedArticles))

    return updeep({
      articles: {
        [savedArticle.article]: {
          savedArticle: savedArticle.id
        }
      }
    }, state)

  default:
    return state
  }
}

// Updates an entity cache in response to any action with response.entities.
const entities = (state = { players: {} }, action) => {
  const { type, response, schema, meta } = action
  let ret = state
  if (response && response.entities) { ret = updeep(response.entities, ret) }

  switch(type) {
  case ActionTypes.CREATE_ITEM_SUCCESS:
    return createItemEntitiesReducer(ret, action)

  case ActionTypes.ITEM_REQUEST:
    return updeep({[schema.key]: {[meta.id]: { isFetching: true}}}, ret)

  case ActionTypes.ITEM_SUCCESS:  
    return updeep({[schema.key]: {[meta.id]: { isFetching: false }}}, ret)

  case ActionTypes.ITEM_ERROR:
    return updeep({[schema.key]: {[meta.id]: { error: true, isFetching: false }}}, ret)

  default:
    return ret
  }
}

// Updates error message to notify about the failed fetches.
const errorMessage = (state = null, action) => {
  const { type, error } = action

  if (type === ActionTypes.RESET_ERROR_MESSAGE) {
    return null
  } else if (error) {
    return error
  }

  return state
}

const setPagesToStale = (entity, key, state) => {
  const pages = get(state, `${entity}.${key}.pages`)
  if(!pages) { return state }

  const update = {}
  keys(pages).forEach( index => update[index] = {hasStaleData: true})

  return updeep({[entity] : { [key]: { pages: update }}}, state)
}

const refreshPagination = (state, key) => {
  const refreshKeys = keys(state[key])
  let ret = state
  refreshKeys.forEach(k => ret = setPagesToStale(key, k, ret))
  return ret
}

const mutateItemPaginationReducer = (state, action) => {
  const { schema, meta } = action

  const _schema = schema || meta.schema
  if(!_schema) { return state }

  // TODO use this in future
  switch(_schema) {
  case Schemas.PROJECT:
    return refreshPagination(state, 'projects')

  case Schemas.STREAM:
    return refreshPagination(state, 'streams')

  case Schemas.RECOMMENDER_RULE:
    return refreshPagination(state, 'recommenderRules')

  default:
    return state
  }
}

// Updates the pagination data for different actions.
const pagination = (state={}, action) => {
  const { type, schema, response, meta, error } = action
  const entityKey = get(schema, '0.key')

  switch(type) {
  case ActionTypes.ITEMS_REQUEST:
    const ids = get(
      state, 
      [entityKey, meta.key, 'pages', meta.pageIndex, 'ids'], 
    [])

    let update = {
      pageSize: meta.pageSize,
      isFetching: true,
      pages: { [meta.pageIndex]: { isFetching: true, ids, staleData: false }}
    }

    return updeep({ [entityKey]: { [meta.key]: update } }, state)

  case ActionTypes.ITEMS_SUCCESS:
    let pages = {[meta.pageIndex]: {
      isFetching: false, ids: response.result, hasStaleData: false
    }}

    return updeep({
      [entityKey]: {
        [meta.key]: {
          isFetching: false,
          totalItems: response.totalItems,
          pages,
        }
      }
    }, state)

  case ActionTypes.ITEMS_FAILURE:
    return updeep({
      [entityKey]: {
        [meta.key]: {
          isFetching: false,
          pages: updeep.constant({}),
          lastError: error,
          [meta.pageIndex]: { error: true },
        }
      }
    }, state)


  case ActionTypes.CREATE_ITEM_SUCCESS:
  case ActionTypes.DELETE_ITEM_SUCCESS:
    return mutateItemPaginationReducer(state, action)

  case ActionTypes.UPDATE_ITEM_SUCCESS:
  // brute force... this should be changed in the reducer to update
  // the specific item, e.g. isActive
    return refreshPagination(state, 'projects')

  case ActionTypes.ITEMS_REFRESH:
    return refreshPagination(state, action.key)

  default:
    return state
  }
}

const authentication = (state={isAuthenticated: false}, action) => {
  const { type, json, data, error } = action

  if (error && error.detail && error.detail.indexOf("Invalid token") >= 0) {
    localStorage.setItem('authentication', "")
    return {
      token: '',
      username: '',
      isAuthenticated: false,
    }
  }

  switch(type) {
  case ActionTypes.LOGIN_REQUEST:
    return {
      token: null,
      username: null,
      isAuthenticated: false,
    }

  case ActionTypes.LOGIN_SUCCESS:
    return {
      token: json.token,
      username: data.username,
      isAuthenticated: true,
    }

  case ActionTypes.RESTORE_STATE_KEY:
    if(action.key === 'authentication') {
      return action.value
    } else {
      return state
    }

  case ActionTypes.REFRESH_TOKEN_SUCCESS:
    return {...state, token: json.token}

  default:
    return state
  }
}

const ui = (state = {
  searchSuggestions: {
    players: [{
      name: 'id contains',
      keywords: 'id ref_key',
      template: 'ref_key__contains:"YOUR_INPUT"',
      lookupKey: 'ref_key__contains',
    }, {
      name: 'sex is MALE',
      keywords: 'sex',
      template: 'sex:"MALE"',
      lookupKey: 'sex',
    }, {
      name: 'sex is FEMALE',
      keywords: 'sex FEMALE',
      template: 'sex:"FEMALE"',
      lookupKey: 'sex',
    }, {
      name: 'age is between',
      keywords: '',
      template: 'age__range:"LOW_AGE,HIGH_AGE"',
      lookupKey: 'age__range',
    }, {
      name: 'age is less than',
      keywords: '',
      template: 'age__lt:"AGE"',
      lookupKey: 'age__lt',
    }, {
      name: 'age is greater than',
      keywords: '',
      template: 'age__gt:"AGE"',
      lookupKey: 'age__gt',
    }, {
      name: 'rating is',
      keywords: '',
      template: 'manual_rating__in:"1,2,3,4,5"',
      lookupKey: 'manual_rating__in',
    }, {
      name: 'country is',
      keywords: '',
      template: 'country__in:"DE,AT"',
      lookupKey: 'country__in',
    }, {
      name: 'total prize is between',
      keywords: '',
      template: 'prize_total__range:"VALUE_1,VALUE_2"',
      lookupKey: 'prize_total__range',
    }, {
      name: 'total prize is less than',
      keywords: '',
      template: 'prize_total__lt:"VALUE"',
      lookupKey: 'prize_total__lt',
    }, {
      name: 'total prize is greater than',
      keywords: '',
      template: 'prize_total__gt:"VALUE"',
      lookupKey: 'prize_total__gt',
    }, {
      name: 'profits are between',
      keywords: '',
      template: 'profits__range:"VALUE_1,VALUE_2"',
      lookupKey: 'profits__range',
    }, {
      name: 'profits are less than',
      keywords: '',
      template: 'profits__lt:"VALUE"',
      lookupKey: 'profits__lt',
    }, {
      name: 'profits are greater than',
      keywords: '',
      template: 'profits__gt:"VALUE"',
      lookupKey: 'profits__gt',
    }, {
      name: 'total tickets are between',
      keywords: '',
      template: 'tickets_total__range:"VALUE_1,VALUE_2"',
      lookupKey: 'tickets_total__range',
    }, {
      name: 'total tickets are less than',
      keywords: '',
      template: 'tickets_total__lt:"VALUE"',
      lookupKey: 'tickets_total__lt',
    }, {
      name: 'total tickets are greater than',
      keywords: '',
      template: 'tickets_total__gt:"VALUE"',
      lookupKey: 'tickets_total__gt',
    }],
    bets: [{
      name: 'id contains',
      keywords: 'id ref_key',
      template: 'ref_key__contains:"YOUR_INPUT"',
      lookupKey: 'ref_key__contains',
    }]
  }
}, action) => {
  return state
}


const rootReducer = combineReducers({
  entities,
  pagination,
  ui,
  authentication,
  errorMessage,
  routing
})

export default rootReducer



// WEBPACK FOOTER //
// ./src/reducers/index.js